import BigNumber from 'bignumber.js';
import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { getDepositRecommendedCurrencies } from '@flipster/apis/v1/wallet';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { UsdtCurrency } from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { isActionAllowedForRegulation } from '../../../currency/is-action-allowed-for-regulation';
import { v1CurrenciesBunja } from '../../../currency/use-v1-currencies';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '../../../earn/state';
import { userRegulationBunja } from '../../../regulation';
import { WalletModalTypeScope } from '../core/state';

export const selectAssetBunja = bunja(() => {
  const type = bunja.use(WalletModalTypeScope);
  const { earnSummaryAtom } = bunja.use(earnSummaryBunja);
  const { currenciesAtom } = bunja.use(v1CurrenciesBunja);
  const { walletsAtom } = bunja.use(walletsBunja);
  const { getUserRegulationAtom } = bunja.use(userRegulationBunja);

  const keywordAtom = atom<string>('');

  const recommendedCurrenciesBaseAtom = atomWithQuery(() => {
    return {
      queryKey: ['getDepositRecommendedCurrencies'],
      queryFn: getDepositRecommendedCurrencies,
      select: (res) => res.data,
    };
  });
  const recommendedCurrenciesAtom = atom((get) => get(recommendedCurrenciesBaseAtom).data?.recommendedCurrencies ?? []);

  const earnAppliedAprByCurrencyAtom = atom((get) => {
    const earnSummary = get(earnSummaryAtom);
    if (!earnSummary) return {};
    const result: Record<string, BigNumber> = {};
    for (const c of earnSummary.currencies) {
      const { appliedAprPct } = getEarnSummaryOfCurrency(c.currency, earnSummary);
      if (appliedAprPct.gt(0)) result[c.currency] = appliedAprPct;
    }
    return result;
  });

  const availableItemsAtom = atom((get) => {
    const currencies = get(currenciesAtom);
    const wallet = get(walletsAtom);
    const regulationStatus = get(getUserRegulationAtom).data?.regulationStatus ?? null;
    return (currencies ?? []).filter((item) => {
      // deposit 은 available 한 모든 코인
      if (type === 'deposit')
        return isActionAllowedForRegulation(item.depositAllowedRegulationStatuses, regulationStatus);
      // withdraw, conversion 은 available & balance > 0인 모든 코인
      const currencyWallet = wallet?.[item.currency];
      const hasBalance = Number(currencyWallet?.walletBalanceWithdrawable) > 0;
      if (type === 'withdraw')
        return isActionAllowedForRegulation(item.withdrawalAllowedRegulationStatuses, regulationStatus) && hasBalance;
      if (type === 'convert')
        return isActionAllowedForRegulation(item.conversionAllowedRegulationStatuses, regulationStatus) && hasBalance;
      return false;
    });
  });

  const itemsAtom = atom((get) => {
    const availableItems = get(availableItemsAtom);
    const keyword = get(keywordAtom);
    const earnAppliedAprByCurrency = get(earnAppliedAprByCurrencyAtom);
    const wallet = get(walletsAtom);
    const items = (() => {
      if (get(keywordAtom).trim()) {
        return availableItems.filter((item) => {
          const lowerKeyword = keyword.toLowerCase();
          return (
            item.englishName.toLowerCase().includes(lowerKeyword) ||
            item.currency.toLowerCase().includes(lowerKeyword) ||
            item.keywords?.some((x) => x.toLowerCase().includes(lowerKeyword))
          );
        });
      }
      return availableItems;
    })();
    return items.sort((a, b) => {
      const aEarnApr = earnAppliedAprByCurrency[a.currency];
      const bEarnApr = earnAppliedAprByCurrency[b.currency];
      if (aEarnApr && !bEarnApr) return -1;
      if (!aEarnApr && bEarnApr) return 1;
      if (aEarnApr && bEarnApr) {
        if (aEarnApr.isGreaterThan(bEarnApr)) return -1;
        if (aEarnApr.isLessThan(bEarnApr)) return 1;
      }
      const aWallet = wallet?.[a.currency];
      const bWallet = wallet?.[b.currency];
      if (!aWallet || !bWallet) return 0;
      const balanceField =
        type === 'withdraw' ? 'walletBalanceWithdrawableInUsdt' : 'walletBalanceExclWithdrawalPendingInUsdt';
      const minus = Num(bWallet?.[balanceField]).minus(aWallet[balanceField]);
      const UsdcCurrency = 'USDC';
      if (!minus.isZero()) return minus.toNumber();
      if (a.currency === UsdtCurrency) return -1;
      if (b.currency === UsdtCurrency) return 1;
      if (a.currency === UsdcCurrency) return -1;
      if (b.currency === UsdcCurrency) return 1;
      return a.currency.localeCompare(b.currency);
    });
  });

  const isEmptyAvailableAssetAtom = atom((get) => get(availableItemsAtom).length === 0);
  const isNoDataAtom = atom((get) => !get(isEmptyAvailableAssetAtom) && get(itemsAtom).length === 0);

  return {
    keywordAtom,
    itemsAtom,
    recommendedCurrenciesAtom,
    earnAppliedAprByCurrencyAtom,
    isEmptyAvailableAssetAtom,
    isNoDataAtom,
  };
});
selectAssetBunja.debugLabel = 'selectAssetBunja';
